import { QuestionCircleOutlined } from '@ant-design/icons';
import { Tooltip } from 'antd';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import _, { cloneDeep } from 'lodash';
import moment from 'moment';
import { v4 as uuidv4 } from 'uuid';

import { resolveAdsDimFilterId } from './adsDims';
import { formatNumberWithSpaces, formatPeriodDate } from './functions';
import { getMetricTrendDirection } from './periodAnalysis';
import { FORMAT, isDate, isMonth, isPeriod, isQuart, isWeekday, monthSort, weekdaySort, isNumericInterval, numericIntervalSort, isPeriodGrouping, periodGroupingSort } from './time';
import { resolveUtmFilterId } from './utmSections';
import { IChartData } from '../../app/providers/redux/slices/reports/chart/types';
import { IFilter } from '../../app/providers/redux/slices/reports/params/filter/types';
import { ITableDataGeneric } from '../../app/providers/redux/slices/reports/table/types';
import { isCompareMetricKey, toBaseMetricKey } from '../../shared/common/constants/compare';
import FilterItem from '../../shared/common/components/reports/params/filter/FilterItem';
import IconFilter from '../../shared/common/components/reports/params/filter/IconFilter';
import {
  averageFormatGroup,
  covertFormatGroup,
  moneyFormatGroup,
  nativeFormatGroup,
  percentFormatGroup,
  summaryFormatGroup,
} from '../../shared/common/constants/groups';
import { groups } from '../../shared/common/constants/reports';
import { getAxisGroupForMetric } from '../../shared/common/components/reports/charts/MetricAxisGroups';
import { data as filtersData } from '../../shared/common/constants/filters';

dayjs.extend(customParseFormat);

/**
 * Строит карту id фильтра -> цвет сущности
 * Рекурсивно обходит дерево фильтров и собирает все id с их цветами
 */
const buildFilterColorMap = (filters: any[], parentColor?: string): Map<string, string> => {
  const colorMap = new Map<string, string>();
  
  for (const filter of filters) {
    const color = filter.color || parentColor;
    
    if (filter.id && color) {
      // Добавляем обычный id
      colorMap.set(filter.id, color);
      // Добавляем id с префиксом not_ для исключений
      colorMap.set(`not_${filter.id}`, color);
    }
    
    if (filter.children && Array.isArray(filter.children)) {
      const childMap = buildFilterColorMap(filter.children, color);
      childMap.forEach((value, key) => colorMap.set(key, value));
    }
  }
  
  return colorMap;
};

// Кэшируем карту цветов для производительности
let filterColorMapCache: Map<string, string> | null = null;

const getFilterColorMap = (): Map<string, string> => {
  if (!filterColorMapCache) {
    filterColorMapCache = buildFilterColorMap(filtersData);
  }
  return filterColorMapCache;
};

/**
 * Мигрирует старые сохранённые фильтры, обновляя поле color
 * на актуальное значение из filters.ts. Это нужно для:
 * 1. Корректной группировки фильтров-исключений по сущностям
 * 2. Обновления старых цветов на новые
 */
export const migrateFiltersWithColor = (filters: any[]): any[] => {
  if (!Array.isArray(filters)) return filters;

  const colorMap = getFilterColorMap();

  return filters.map(filter => {
    const migrated = { ...filter };

    // Обновляем цвет на актуальный
    const actualColor = colorMap.get(migrated.id);
    if (actualColor) {
      migrated.color = actualColor;
    } else if (
      resolveUtmFilterId(String(migrated.id).replace(/^not_/, ''))
      || resolveAdsDimFilterId(String(migrated.id).replace(/^not_/, ''))
    ) {
      // Фильтры «Параметров» и «Срезов Яндекс.Директа» встраиваются только в отчёт рекламы,
      // в статическом дереве их нет.
      // Цвет берём у сущности «Реклама» — по её штатному фильтру «Дата рекламы».
      const adColor = colorMap.get('ads_data');
      if (adColor) {
        migrated.color = adColor;
      }
    }

    // Очищаем &nbsp; в строковых полях
    if (typeof migrated.name === 'string') {
      migrated.name = migrated.name.replace(/&nbsp;/g, ' ');
    }
    if (typeof migrated.full_name === 'string') {
      migrated.full_name = migrated.full_name.replace(/&nbsp;/g, ' ');
    }

    // Удаляем searchName если это React-элемент (объект с type/props/_owner)
    if (migrated.searchName && typeof migrated.searchName === 'object' && migrated.searchName !== null) {
      delete migrated.searchName;
    }

    // Очищаем &nbsp; в checkeds и items
    if (Array.isArray(migrated.checkeds)) {
      migrated.checkeds = migrated.checkeds.map((item: any) => {
        if (!item || typeof item !== 'object') return item;
        const cleaned = { ...item };
        if (typeof cleaned.name === 'string') {
          cleaned.name = cleaned.name.replace(/&nbsp;/g, ' ');
        }
        return cleaned;
      });
    }
    if (Array.isArray(migrated.items)) {
      migrated.items = migrated.items.map((item: any) => {
        if (!item || typeof item !== 'object') return item;
        const cleaned = { ...item };
        if (typeof cleaned.name === 'string') {
          cleaned.name = cleaned.name.replace(/&nbsp;/g, ' ');
        }
        return cleaned;
      });
    }

    return migrated;
  });
};

const recurseChildrenFilter = (rows: any, filters: any) => {
  return rows
    .filter((item: any) => {
      return Object.entries(filters).every(([ key, val ]: any) => {
        let check;
        switch (val.action) {
          case 'less':
            check = item[key] < val.value;
            break;
          case 'equal':
            check = item[key] === val.value;
            break;
          case 'over':
            check = item[key] > val.value;
            break;

          default:
            check = true;
        }
        return check;
      });
    })
    .map((row: any) => {
      if (row.children && row.children.length > 0) {
        row.children = recurseChildrenFilter(row.children, filters);
      }
      return row;
    });
};

const recurseChildrenSort = (rows: any, funSort: any) => {
  if (!Array.isArray(rows) || rows.length === 0) return rows;
  const newRows = cloneDeep(rows);

  newRows.sort(funSort);
  for (let index = 0; index < newRows.length; index++) {
    const row = newRows[index];
    if (row.children && row.children.length > 0) {
      row.children = recurseChildrenSort(row.children, funSort);
    }
  }
  return newRows;
};

/**
 * Рекурсивно сортирует строки с автоопределением типа данных на каждом уровне
 */
const recurseChildrenSortAuto = (rows: any, sortType: 'ASC' | 'DESC') => {
  if (!Array.isArray(rows) || rows.length === 0) return rows;
  const newRows = cloneDeep(rows);

  // Определяем тип данных для текущего уровня
  const firstValueGroup = newRows[0]?.['group'];
  const lastValueGroup = newRows[newRows.length - 1]?.['group'];
  
  // Определяем функцию сортировки для текущего уровня
  let sortFn: (a: any, b: any) => number;
  
  // Проверяем ВСЕ элементы (не только первые) на числовые интервалы
  const allGroups = newRows.map((r: any) => r['group']);
  const hasNumericIntervals = allGroups.some((g: any) => isNumericInterval(g));
  
  // Проверяем числовые интервалы ("1 день", "2 дня", "10 дней", "> 15 дней") - ПРИОРИТЕТ №1
  if (hasNumericIntervals) {
    sortFn = (a: any, b: any) => numericIntervalSort(a['group'], b['group'], sortType);
  }
  // Проверяем предопределенные периоды ("День от регистрации до заказа")
  else if (isPeriodGrouping(firstValueGroup) || isPeriodGrouping(lastValueGroup)) {
    sortFn = (a: any, b: any) => periodGroupingSort(a['group'], b['group'], sortType);
  }
  // Проверяем дни недели
  else if (isWeekday(lastValueGroup)) {
    sortFn = (a: any, b: any) => weekdaySort(a['group'], b['group'], sortType);
  }
  // Проверяем кварталы
  else if (isQuart(lastValueGroup)) {
    sortFn = (a: any, b: any) => {
      const A: string | undefined = a['group'];
      const B: string | undefined = b['group'];
      if (!A || !B) return 1;
      const numQuartA = Number(A.slice(A.length - 3) + A.slice(0, 1));
      const numQuartB = Number(B.slice(B.length - 3) + B.slice(0, 1));
      return sortType === 'ASC' ? numQuartA - numQuartB : numQuartB - numQuartA;
    };
  }
  // Проверяем месяцы
  else if (newRows.some((el: any) => isMonth(el['group']))) {
    sortFn = (a: any, b: any) => monthSort(a['group'], b['group'], sortType);
  }
  // Проверяем периоды дат
  else if (isPeriod(lastValueGroup)) {
    sortFn = (a: any, b: any) => {
      const datesA = a['group']?.split('&nbsp;-&nbsp;');
      const datesB = b['group']?.split('&nbsp;-&nbsp;');
      if (!datesA || !datesB) return 1;
      const startDateA = dayjs(datesA[0], FORMAT);
      const startDateB = dayjs(datesB[0], FORMAT);
      return sortType === 'ASC' 
        ? (startDateA.isAfter(startDateB) ? 1 : -1)
        : (startDateA.isAfter(startDateB) ? -1 : 1);
    };
  }
  // Проверяем даты
  else if (isDate(lastValueGroup)) {
    sortFn = (a: any, b: any) => {
      return sortType === 'ASC'
        ? (dayjs(a['group']).isAfter(dayjs(b['group'])) ? 1 : -1)
        : (dayjs(a['group']).isAfter(dayjs(b['group'])) ? -1 : 1);
    };
  }
  // Обычная строковая сортировка
  else {
    sortFn = (a: any, b: any) => {
      if (sortType === 'ASC') {
        if (a.group < b.group) return -1;
        if (a.group > b.group) return 1;
        return 0;
      } else {
        if (a.group < b.group) return 1;
        if (a.group > b.group) return -1;
        return 0;
      }
    };
  }

  // Сортируем текущий уровень
  newRows.sort(sortFn);
  
  // Рекурсивно сортируем детей с автоопределением типа
  for (let index = 0; index < newRows.length; index++) {
    const row = newRows[index];
    if (row.children && row.children.length > 0) {
      row.children = recurseChildrenSortAuto(row.children, sortType);
    }
  }
  
  return newRows;
};

/**
 * Рекурсивно рассчитывает проценты для строк таблицы.
 * На каждом уровне группировки проценты рассчитываются относительно родительской строки,
 * а не относительно общего итога.
 * 
 * @param rows - массив строк текущего уровня
 * @param percentages - массив ключей метрик, для которых нужно показывать проценты
 * @param baseRow - базовая строка для расчёта процентов (для первого уровня - totalRow, для вложенных - родительская строка)
 * @param originalRows - оригинальные строки с числовыми значениями (до преобразования в проценты) для расчёта базы дочерних элементов
 */
const recurseChildrenPercent = (
  rows: any,
  percentages: any,
  baseRow: any,
  originalRows?: any
) => {
  // Если originalRows не передан, создаём глубокую копию для сохранения оригинальных значений
  const origRows = originalRows || JSON.parse(JSON.stringify(rows));

  for (let index = 0; index < rows.length; index++) {
    const row = rows[index];
    const origRow = origRows[index];

    percentages.forEach((percKey: any) => {
      if (
        typeof row[percKey] === 'number' &&
        typeof baseRow[percKey] === 'number' &&
        baseRow[percKey] > 0
      ) {
        row[percKey] =
          parseFloat(String((row[percKey] / baseRow[percKey]) * 100)).toFixed(
            2
          ) + ' %';
      } else if (
        typeof row[percKey] === 'string' &&
        row[percKey].includes('.')
      ) {
        const parsedVal = parseFloat(row[percKey]);
        const parsedBaseVal = parseFloat(baseRow[percKey]);

        if (parsedBaseVal > 0) {
          row[percKey] =
            parseFloat(String((parsedVal / parsedBaseVal) * 100)).toFixed(2) +
            ' %';
        } else {
          row[percKey] = '0.00 %';
        }
      } else {
        row[percKey] = '0.00 %';
      }
    });

    // Для дочерних элементов используем оригинальные значения текущей строки как базу
    if (row.children && row.children.length > 0) {
      recurseChildrenPercent(row.children, percentages, origRow);
    }
  }

  return rows;
};
export const recurseChildrenEmpty = (rows: any) => {
  for (let index = 0; index < rows.length; index++) {
    const row = rows[index];
    if (row.children && row.children.length > 0) {
      recurseChildrenEmpty(row.children);
    }
    if (row.children && row.children.length === 0) {
      row.children.push({
        group: 'empty',
        pathStr: row.pathStr + '-empty',
        parentId: row.id,
      });
    }
  }
  return rows;
};

export const sortByGroup = (exData: ITableDataGeneric[], sortType: 'ASC' | 'DESC') => {
    let data = [ ...exData ];
    
    // Используем автоматическое определение типа данных на каждом уровне вложенности
    data = recurseChildrenSortAuto(data, sortType);

    return data;
};

export const finishRows = ({
  rows = [],
  filters = {},
  sort = {},
  percentages = [],
  totalRow,
  totalChoosenRow,
}: any) => {
  // копирую многомерный массив
  let data = JSON.parse(JSON.stringify(rows));
   const totalChoosenRowData = {
    group: 'Итого и среднее выбранных',
    pathStr: 'total_choosen',
    ...totalChoosenRow,
  };

    const totalRowData = {
    group: `Итого и среднее <span style="color: gray;padding-left: 8px;">${rows?.length} строк</span>`,
    pathStr: 'total',
    ...totalRow,
  };

  // фильтрация
  if (Object.keys(filters).length > 0) {
    data = recurseChildrenFilter(data, filters);
  }

  // сортировка
  if (Object.keys(sort).length > 0) {
    const sortItem = Object.entries<'ASC' | 'DESC'>(sort)[0];
    if (sortItem[0] === 'group') {
      data = sortByGroup(data, sortItem[1]);
    } else {
      if (sortItem[1] === 'ASC') {
        data = recurseChildrenSort(
          data,
          (a: any, b: any) => a[sortItem[0]] - b[sortItem[0]]
        );
        if (
          data.length > 0 &&
          data[0] !== undefined &&
          typeof data[0][sortItem[0]] === 'string'
        ) {
          const sortItemValue = data[0][sortItem[0]];

          if (
            sortItemValue.includes &&
            (sortItemValue.includes(' +') || sortItemValue.includes(' -'))
          ) {
            data = recurseChildrenSort(data, (a: any, b: any) => {
              const numA = parseFloat(a[sortItem[0]].match(/\d+\.?\d*/)[0]);
              const numB = parseFloat(b[sortItem[0]].match(/\d+\.?\d*/)[0]);
              return numA - numB;
            });
          }
        }
      } else {
        data = recurseChildrenSort(
          data,
          (a: any, b: any) => b[sortItem[0]] - a[sortItem[0]]
        );
        if (
          data.length > 0 &&
          data[0] !== undefined &&
          typeof data[0][sortItem[0]] === 'string'
        ) {
          const sortItemValue = data[0][sortItem[0]];

          if (
            sortItemValue.includes &&
            (sortItemValue.includes(' +') || sortItemValue.includes(' -'))
          ) {
            data = recurseChildrenSort(data, (a: any, b: any) => {
              const numA = parseFloat(a[sortItem[0]].match(/\d+\.?\d*/)[0]);
              const numB = parseFloat(b[sortItem[0]].match(/\d+\.?\d*/)[0]);
              return numB - numA;
            });
          }
        }
      }
    }
    
  }

  // проценты
  if (percentages.length > 0) {
    data = recurseChildrenPercent(data, percentages, totalRow);
  }

  // добавляю empty когда пусто в результатах группы
  data = recurseChildrenEmpty(data);
  totalChoosenRow ? data.unshift(totalChoosenRowData) : null;
  data.unshift(totalRowData);
  return data;
};

export const loopSearch = (data: any, searchValue: string) => {
  try {
    const result = data.map((item: any) => {
      const reg = new RegExp(searchValue, 'i');
      const index = item.label.search(reg);
      const beforeStr = item.label.substr(0, index);
      const searchStr = item.label.substr(index, searchValue.length);

      const alignString =
        typeof item.name === 'object'
          ? item.name.props.children[0].substr(index + searchValue.length)
            ? 'inherit'
            : 'space-between'
          : 'inherit';
      const afterStr =
        typeof item.name === 'object' ? (
          <span
            className="group-item-icon-wrapper"
            style={{ width: alignString === 'inherit' ? '100%' : 'auto' }}
          >
            <span
              style={{
                display: 'block',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
                maxWidth: 190,
              }}
            >
              {item.name.props.children[0]?.substr(index + searchValue.length)}
            </span>
            {item.name.props.children[1]}
          </span>
        ) : (
          item.name?.substr(index + searchValue.length)
        );

      let title = item.name;

      if (index > -1 && searchValue) {
        title = (
          <span
            style={{
              display: 'flex',
              justifyContent: alignString,
              flexDirection: 'row',
              flexWrap: 'nowrap',
              backgroundColor: '#fec047',
            }}
          >
            {beforeStr}
            {/*<span className="tree-search-value">*/}
            {searchStr}
            {/*</span>*/}
            {afterStr}
          </span>
        );
      }

      if (item.children) {
        return {
          ...item,
          title,
          children: loopSearch(item.children, searchValue),
        };
      }

      return {
        ...item,
        title: title,
        isLeaf: true,
      };
    });
    return result;
  } catch (err) {
    console.error(err);
  }
  return [];
};


export const getChildrenKey = (dataKey: string, data: any) => {
  let res = false;

  data.forEach((item: any) => {
    if (item.children) {
      res = getChildrenKey(dataKey, item.children);
    } else {
      if (item.dataKey === dataKey) {
        res = item.key;
      }
    }
  });

  return res;
};

/** Базовые ключи группировок с персональными данными (без user_id — ID пользователя остаётся доступным) */
const PERSONAL_DATA_BASE_KEYS = ['user_email', 'user_id_ext'];
/** Суффиксы для разных типов отчётов: universal/ad='', order='_2', payment='_3', webinar='_4', visit='_6' */
const REPORT_SUFFIXES = ['', '_2', '_3', '_4', '_6'];
/** Все ключи персональных данных (базовые + с суффиксами для всех типов отчётов) */
export const PERSONAL_DATA_KEYS = PERSONAL_DATA_BASE_KEYS.flatMap(
  (key) => REPORT_SUFFIXES.map((suffix) => key + suffix)
);

export const filterGroupsDisabledData = (data: any, hiddenKeys: string[]) => {
  // Создаём глубокую копию данных, чтобы не мутировать исходные
  const loop = (loopData: any[]): any[] => {
    return loopData.map((item) => {
      const newItem = {
        ...item,
        disableCheckbox: hiddenKeys.includes(item.dataKey),
      };
      if (item.children) {
        newItem.children = loop(item.children);
      }
      return newItem;
    });
  };

  return loop(data);
};

export const disabledMinutes = () => {
  let result = [];

  for (let i = 0; i < 60; i++) {
    if (i % 5 !== 0) result.push(i);
  }

  return result;
};

export const updateFilterItemInList = (
  filters: IFilter[],
  index: number,
  payload: any
) => {
  return filters.map((item, indexArr) => {
    return index === indexArr ? payload : item;
  });
};

export const getParentKey = (id: string, tree: any): string => {
  let parentKey;

  for (let i = 0; i < tree.length; i++) {
    const node = tree[i];

    if (node.children) {
      if (node.children.some((item: any) => item.id === id)) {
        parentKey = node.key;
      } else if (getParentKey(id, node.children)) {
        parentKey = getParentKey(id, node.children);
      }
    }
  }

  return parentKey;
};

export const getMetricsParentKey = (id: string, tree: any): string => {
  let parentKey;

  for (let i = 0; i < tree.length; i++) {
    const node = tree[i];

    if (node.children) {
      if (node.children.some((item: any) => item.key === id)) {
        parentKey = node.key;
      } else if (getParentKey(id, node.children)) {
        parentKey = getParentKey(id, node.children);
      }
    }
  }

  return parentKey;
};

export const generateListKeys = (data: any) => {
  let dataList: any = [];

  function getTemp(data: any) {
    for (let i = 0; i < data.length; i++) {
      const node = data[i];
      // Добавляем axisGroup для метрик на основе dataKey или name
      const axisGroup = node.dataKey 
        ? getAxisGroupForMetric(node.dataKey) 
        : (node.name ? getAxisGroupForMetric(node.name) : undefined);
      
      dataList.push({ 
        ...node,
        ...(axisGroup !== undefined && { axisGroup })
      });

      if (node.children) {
        getTemp(node.children);
      }
    }
  }

  getTemp(data);

  return dataList;
};

export const declOfNum = (number: number, titles: string[]) => {
  const cases = [ 2, 0, 1, 1, 1, 2 ];

  return titles[
    number % 100 > 4 && number % 100 < 20
      ? 2
      : cases[number % 10 < 5 ? number % 10 : 5]
  ];
};

const TitleRoot = ({ color, icon, searchName, description }: any) => {
  return (
    <strong
      style={{
        color,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}
    >
      <span>
        <IconFilter icon={icon} />
        &nbsp;{searchName}
      </span>

      {description ? (
        <span
          style={{
            float: 'right',
            marginRight: 20,
            display: 'inline-flex',
            alignItems: 'center',
          }}
        >
          <Tooltip
            placement="bottom"
            title={
              <div
                className="page-title-tooltip-text"
                dangerouslySetInnerHTML={{ __html: description }}
              ></div>
            }
            color="#fff"
            zIndex={9999}
            overlayClassName="page-title-tooltip"
          >
            <QuestionCircleOutlined style={{ color: '#ccc', fontSize: 16 }} />
          </Tooltip>
        </span>
      ) : null}
    </strong>
  );
};

export const prepareGroupsData = (data: any, _preKey?: any): any[] => {
  const preKey = _preKey || '0';

  // Создаём новый массив, чтобы не мутировать исходные данные
  return data.map((item: any, i: number) => {
    // Используем dataKey как ключ для leaf-узлов (стабильные ключи),
    // позиционный ключ для parent-узлов
    const key = item.dataKey || `${preKey}-${i}`;
    const newItem = {
      ...item,
      key,
      label: item.name,
    };

    // выключаю check в родительских элементах
    if (item.children) {
      newItem.checkable = false;
      newItem.children = prepareGroupsData(item.children, key);
    }

    return newItem;
  });
};

/**
 * Подготавливает данные для Tree компонента с использованием dataKey как ключа.
 * Для leaf-узлов: key = dataKey (например, 'reg_utm_source')
 * Для parent-узлов: key = name (например, 'Регистрации')
 * 
 * Это обеспечивает стабильные ключи, которые не зависят от порядка элементов.
 */
export const prepareTreeData = (data: any[]): any[] => {
  return data.map((item) => {
    // Используем dataKey как ключ для leaf-узлов, name для parent-узлов
    const key = item.dataKey || item.name;
    const newItem = {
      ...item,
      key,
      label: item.name,
    };
    
    if (item.children) {
      newItem.checkable = false;
      newItem.children = prepareTreeData(item.children);
    }
    
    return newItem;
  });
};


const EXTRA_FIELDS_FULL_NAMES: string[] =  [
  'Доп. поля регистрации',
  'Доп. поля пользователя',
  'Доп. поля заказа',
  'Доп. поля заявки',
];


const markExtraFieldsChildren = (nodes: any[]): void => {
  if (!Array.isArray(nodes)) return;

  nodes.forEach((node) => {
    const hasChildren =
      Array.isArray(node.children) && node.children.length > 0;
    const isLeaf = !hasChildren;
     if (isLeaf && EXTRA_FIELDS_FULL_NAMES.includes(node.full_name)) {
        if (!node.id && node.dataKey) {
          node.id = node.dataKey;
        }

 
      if (!node.type) {
        node.type = 'lazylist';
      }

      if (node.list === undefined) {
        node.list = true;
      }
      if (node.showListPlus === undefined) {
        node.showListPlus = true;
      }
        node.full_name = node.name;
      
    }

    if (hasChildren) {
      markExtraFieldsChildren(node.children);
    }

    
  });
};







export const prepareData = (
  data: any,
  searchValue: string,
  filters: { [key: string]: any }[],
  disableAttr: boolean,
  _fullName?: string,
  _color?: string,
  _icon?: any,
  _preKey?: string,
  _namePath?: string[],
  _description?: string,
  filterParent?: any,
  onOk?: any,
  isModal?: boolean,
  isYM?: boolean,
  isLocalMode?: boolean,
  localFilters?: any[],
  setLocalFilters?: (filters: any[]) => void,
) => {
  try {
    const preKey = _preKey || '0';
    for (let i = 0; i < data.length; i++) {
      let item = data[i];
    
      const color = _color || item.color;
      const icon = _icon || item.icon;
      const namePath = _namePath ? [ ..._namePath, item.name ] : [ item.name ];
      const key = `${preKey}-${i}`;
      const description = item.description ?? '';
      const isExtraParent = EXTRA_FIELDS_FULL_NAMES.includes(item.name);

      const isLazyExtraChild =
        !item.children &&
        item.type === 'lazylist' &&
        namePath.some((p) => EXTRA_FIELDS_FULL_NAMES.includes(p));

      let fullName = item?.full_name || _fullName;

      if (isLazyExtraChild) {
        fullName = item.name;
      }

      try {
        item.full_name = fullName;
      } catch (err) {
        console.error(err);
      }

      // поиск
      if (searchValue) {
        const reg = new RegExp(searchValue, 'i');

        const index = item.name.search(reg);
        const beforeStr = item.name.substr(0, index);
        const searchStr = item.name.substr(index, searchValue.length);
        const afterStr = item.name.substr(index + searchValue.length);

        item.searchName =
          index > -1 ? (
            <span>
              {beforeStr}
              <span className="tree-search-value">{searchStr}</span>
              {afterStr}
            </span>
          ) : (
            <span className="tree-search-hide">{item.name}</span>
          );
      } else {
        item.searchName = <span>{item.name}</span>;
      }

      const countSelected = filters.filter((f) => f.path?.includes(item.id));
      const countEl =
        countSelected.length > 0 ? (
          <div className="picker-counter-element" />
        ) : null;
      const activeParent = filterParent?.some(
        (el: any) => el.id === item.id
      ) ? (
        <div className="picker-counter-element" />
      ) : null;
      const countElement = filters.find(
        (el) => el?.namePath[el?.namePath?.length - 2] === item.name
      ) ? (
        <div className="picker-counter-element" />
      ) : null;
      const firstLevel = filters.find((el) => el.namePath[1] === item.name) ? (
        <div className="picker-counter-element" />
      ) : null;
      if (item.root) {
        item.title = () => (
          <div>
            <TitleRoot {...item} /> {countEl || firstLevel}
          </div>
        );
      } else if (item.children || isExtraParent) {
        // Parent-узел: searchName + tooltip с описанием (если visibleDescription)
        item.title = () => (
          <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
            <span>
              {item.searchName} {countEl || activeParent || countElement}
            </span>
            {item.visibleDescription && item.description && (
              <span
                style={{
                  marginRight: 20,
                  display: 'inline-flex',
                  alignItems: 'center',
                }}
              >
                <Tooltip
                  placement="bottom"
                  title={
                    <div
                      className="page-title-tooltip-text"
                      dangerouslySetInnerHTML={{ __html: item.description }}
                    ></div>
                  }
                  color="#fff"
                  zIndex={9999}
                  overlayClassName="page-title-tooltip"
                >
                  <QuestionCircleOutlined
                    style={{ color: '#ccc', fontSize: 16 }}
                  />
                </Tooltip>
              </span>
            )}
          </span>
        );
      } else {
        // Leaf-узел: FilterItem с Popover для выбора значения
        item.title = () => {
          return item.description ? (
            <FilterItem
              onOk={onOk}
              index={i}
              isModal={isModal}
              isLocalMode={isLocalMode}
              localFilters={localFilters}
              setLocalFilters={setLocalFilters}
              {...item}
              isYM={isYM}
              extra={
                <span
                  style={{
                    float: 'right',
                    marginRight: 20,
                    display: 'inline-flex',
                    alignItems: 'center',
                  }}
                >
                  <Tooltip
                    placement="bottom"
                    title={
                      <div
                        className="page-title-tooltip-text"
                        dangerouslySetInnerHTML={{ __html: item.description }}
                      ></div>
                    }
                    color="#fff"
                    zIndex={9999}
                    overlayClassName="page-title-tooltip"
                  >
                    <QuestionCircleOutlined
                      style={{ color: '#ccc', fontSize: 16 }}
                    />
                  </Tooltip>
                </span>
              }
            />
          ) : (
            <FilterItem
              isModal={isModal}
              isLocalMode={isLocalMode}
              localFilters={localFilters}
              setLocalFilters={setLocalFilters}
              index={i} {...item}
              isYM={isYM}
              onOk={onOk}
            />
          );
        };
      }

      item.label = item.name;
      item.color = color;
      item.icon = icon;
      // dataKey для метрик, id для фильтров, name как fallback
      item.key = item.dataKey || item.id || item.name;
      item.namePath = namePath;
      item.description = description;
      if (item.children) {
        item.checkable = false;
        prepareData(
          item.children,
          searchValue,
          filters,
          disableAttr,
          fullName,
          color,
          icon,
          key,
          namePath,
          description,
          filterParent,
          onOk,
          isModal,
          isYM,
          isLocalMode,
          localFilters,
          setLocalFilters
        );
      }
    }

    if (disableAttr) {
      data.forEach((item: any, index: number) => {
        if (
          item.id === 'webinar_reg_days_end' ||
          item.id === 'webinar_reg_days'
        ) {
          data.splice(index, 1);
        }
      });
    }

    markExtraFieldsChildren(data);
  } catch (err) {
    console.error(err);
  }
  return data;
};

export const checkMetricsGroups = (
  metric: any,
  label: string,
  subLabel: string,
  metricsChecked: any
) => {
  let groupName = {
    name: label,
    label: label,
    show: true,
    group: '',
  };

  groups.forEach((group) => {
    group.metrics.forEach((metricItem) => {
      if (
        metric.dataKey ? metric.dataKey.includes(metricItem.name) : metric.name.includes(metricItem.name) &&
        metricsChecked.find((metricChecked: any) =>
          metric.dataKey ? metric.dataKey.includes(metricItem.name) : metric.name.includes(metricChecked.name)
        )
      ) {
        groupName.name = `${group.metrics[0].label} (${subLabel})`;
        groupName.group = group.groupName;
      }
    });

    if ((metric.name || metric.dataKey) === group.metrics[0].name) {
      groupName.name = label;
    }
  });

  return groupName;
};

export const filterAxisShow = (axisList: any) => {
  let finAxisList = axisList;

  groups.forEach((group) => {
    let groupAxisIndexes: any = [];

    finAxisList.forEach((axis: any, index: number) => {
      if (axis.group === group.groupName) {
        finAxisList[index].show = false;
        groupAxisIndexes.push(index);
      }
    });

    finAxisList.forEach((item: any) => {
      if (item.group === group.groupName) {
        item.seriesName = finAxisList[groupAxisIndexes[0]].seriesName;
      }
    });

    if (groupAxisIndexes.length > 0) {
      finAxisList[groupAxisIndexes[0]].show = true;
    }
  });

  finAxisList.forEach((item1: any) => {
    const itemName1 = item1.seriesName.substring(
      0,
      item1.seriesName.indexOf('(') - 1
    );

    finAxisList.forEach((item2: any) => {
      const itemName2 = item2.seriesName.substring(
        0,
        item2.seriesName.indexOf('(') - 1
      );
      if (itemName1 === itemName2 && item1.seriesName !== item2.seriesName) {
        item2.seriesName = item1.seriesName;
        item2.show = false;
      }
    });
  });
  return finAxisList.map((axis: any) => {
    return {
      seriesName: axis.seriesName,
      show: axis.show,
      opposite: true,
      isCustomMetric: axis.isCustomMetric,
      dataKey: axis.dataKey,
    };
  });
};

const formatDetailsKeyInNumber = (detailsKey: string) => {
  switch (detailsKey) {
    case 'd':
      return 1;
    case 'w':
      return 7;
    case 'd-w':
      return 1;
    case 'm':
      return 30;
    case 'q':
      return 90;
    case 'y':
      return 365;
    default:
      return 1;
  }
};

export const predictDotsCount = (
  period: any,
  groupsCount: number,
  metricsCount: number,
  detailsKey: string
) => {
  const details = formatDetailsKeyInNumber(detailsKey);
  const daysCount = moment(period.end).diff(moment(period.start), 'days');

  return (
    (daysCount * (groupsCount === 1 ? metricsCount : groupsCount)) / details
  );
};

export const appendDetails = (detailsKey: string) => {
  let detailsArray = [ 'd', 'w', 'm', 'q', 'y' ];

  if (detailsKey === 'd-w') {
    return 'm';
  }

  let newDetail = detailsKey;

  detailsArray.forEach((item, index) => {
    if (item === detailsKey) {
      if (index === 4) {
        newDetail = detailsArray[index];
      } else {
        newDetail = detailsArray[index + 1];
      }
    }
  });

  return newDetail;
};

export const findRowRecurse = (rows: any, pathStr: string, depth = 0): any => {
  if (!rows || !Array.isArray(rows) || !pathStr) {
    return undefined;
  }

  for (let index = 0; index < rows.length; index++) {
    const row = rows[index];
    if (!row) continue;

    if (row.pathStr === pathStr) {
      return row;
    }
    if (row.children && row.children.length > 0) {
      const found = findRowRecurse(row.children, pathStr, depth + 1);
      if (found) {
        return found;
      }
    }
  }

  return undefined;
};

const numberWithSpaces = (x: string | number) => {
  let parts = x.toString().split('.');
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  return parts.join('.');
};

export const formatCellData = (cellData: number | string, dataKey?: string) => {
  if (cellData === 0) return '0';
  if (!cellData) return '-';

  if (typeof cellData === 'number') {
    return numberWithSpaces(cellData);
  }

  if (
    (cellData.includes('-') || cellData.includes('+')) &&
    cellData.includes('%')
  ) {
    return formatCompareData(`${cellData}`, dataKey);
  }
  if (cellData.includes('%') || cellData.includes('=')) {
    return `${cellData}`;
  }
  const floatData = parseFloat(cellData);
  if (floatData && !cellData.includes('-')) {
    return numberWithSpaces(floatData);
  }
  if (cellData.includes('-')) {
    const formattedCell = cellData.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    return formattedCell;
  }
  return cellData;
};

const formatCompareData = (cellData: string, dataKey?: string) => {
  // Цвет — по общим правилам «рост хорошо/плохо» (periodAnalysis.ts), тем же, что у «Динамики».
  // У метрик школы (dataKey с «_») здесь нет метаданных — как раньше, рост зелёный.
  const direction = dataKey && !dataKey.startsWith('_') ? getMetricTrendDirection(dataKey) : 'up';
  const formattedCell = cellData.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  const regex = /(-?\d{1,3}(?: \d{3})*)(?:\.(\d+))?(%)?/g;
  const values = formattedCell.match(regex);
  let color;
  if (values) {
    if (dataKey) {
      const isFall = values[1].includes('-');
      if (direction === 'neutral') {
        color = '#1890ff';
      } else if (direction === 'down') {
        color = isFall ? 'green' : 'red';
      } else {
        color = isFall ? 'red' : 'green';
      }
    }

    const style = {
      color,
      fontSize: 10,
      marginRight: 5,
      marginLeft: 5,
    };
    return (
      <span className={`column-span ${dataKey}`}>
        {values[0]} <span style={style}>{values[1]}</span>
        {values[2]}
      </span>
    );
  }
};
export const formatSummaryPayments = (string: string, currency: string) => {
  return `${currency} ${string.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}`;
};
export const formatMoneyData = (
  cellData: string | number,
  currency: string,
  dataKey?: string
) => {
  if (typeof cellData === 'string' && cellData.includes('%')) {
    return <>{formatCellData(cellData, dataKey)}</>;
  }
  if (cellData === 0 || cellData === '0') return '0';
  if (!cellData || cellData === '-') return '-';
  if (
    typeof cellData === 'string' &&
    (cellData.includes(' -') || cellData.includes(' +'))
  ) {
    return (
      <>
        {currency} {formatCellData(cellData, dataKey)}
      </>
    );
  }
  return (
    <>
      {currency}{' '}
      {formatCellData(
        parseFloat(cellData + '')
          .toFixed(2)
          .toString(),
        dataKey
      )}
    </>
  );
};

export const formatConvertData = (
  cellData: string | number,
  dataKey?: string
) => {
  if (!cellData && cellData !== 0) return '-';

  if (typeof cellData === 'string') {
    if (
      (cellData.includes('-') || cellData.includes('+')) &&
      cellData.includes('%')
    ) {
      return formatCompareData(`${cellData}`, dataKey);
    }

    if (cellData.includes('%') || cellData.includes('=')) {
      return `${cellData}`;
    }
  }

  return `${typeof cellData === 'number' ? formatNumberWithSpaces(cellData) : cellData}%`;
};

export const calculateMaxMinBubbleChart = (data: any) => {
  let minX = 0;
  let maxX = 0;
  let minY = 0;
  let maxY = 0;

  data.forEach((dataItem: any) => {
    const itemX = dataItem.data[0][0];
    const itemY = dataItem.data[0][1];

    if (itemX > maxX) maxX = itemX;
    if (itemX < minX) minX = itemX;

    if (itemY > maxY) maxY = itemY;
    if (itemY < minY) minY = itemY;
  });

  return {
    maxX: maxX + 30,
    minX: minX - 30,
    maxY: maxY + 30,
    minY: minY - 30,
  };
};

export const buildSeriesForBubbleChart = (
  groupings: any,
  totalGroupings: any,
  chartType: string,
  tableData: any,
  checkedMetrics: any
) => {
  if (chartType !== 'bubble') {
    return [];
  }

  let _series = [];

  if (totalGroupings.length > 0) {
    if (checkedMetrics.length >= 3) {
      if (tableData.total) {
        let x, y, z;

        try {
          x = tableData.total[checkedMetrics[0].name] || 0;
          y = tableData.total[checkedMetrics[1].name] || 0;
          z = tableData.total[checkedMetrics[2].name] || 0;
        } catch (e) {
          x = 0;
          y = 0;
          z = 0;
        }

        const totalFinalSeriesData = [ [ parseInt(x), parseInt(y), parseInt(z) ] ];

        _series.push({ name: 'Всего', data: totalFinalSeriesData });
      }
    }
  }

  if (groupings.length > 0) {
    groupings.forEach((groupItem: any) => {
      if (checkedMetrics.length >= 3) {
        let groupData;

        // Приводим label к строке для безопасной работы с методами строк
        const safeLabel = String(groupItem.label ?? '');
        if (safeLabel.includes('→')) {
          let grops = safeLabel.split('→');
          let parentGroup = tableData.data.find((item: any) =>
            grops[0].includes(item.group)
          );

          const recurrentSearch = (
            grGroup: any,
            searchValues: string[],
            index: number
          ) => {
            if (grGroup.children) {
              let result;
              grGroup.children.forEach((item: any) => {
                if (searchValues[index].includes(item.group)) {
                  if (
                    searchValues[index] ===
                    searchValues[searchValues.length - 1]
                  ) {
                    result = item;
                  } else {
                    result = recurrentSearch(item, searchValues, index + 1);
                  }
                }
              });

              return result;
            } else {
              return grGroup;
            }
          };

          groupData = recurrentSearch(parentGroup, grops, 1);
        } else {
          groupData = tableData.data.find(
            (item: any) => item.group === groupItem.label
          );
        }

        if (groupData) {
          let x, y, z;

          try {
            x = groupData[checkedMetrics[0].name] || 0;
            y = groupData[checkedMetrics[1].name] || 0;
            z = groupData[checkedMetrics[2].name] || 0;
          } catch (e) {
            x = 0;
            y = 0;
            z = 0;
          }

          const finalSeriesData = [ [ parseInt(x), parseInt(y), parseInt(z) ] ];

          _series.push({ name: groupItem.label, data: finalSeriesData });
        }
      }
    });
  }

  return _series;
};

export const checkConvertDataType = (dataIndex: string) => {
  let result = false;
  if (location.pathname.includes('order-reports')) {
    const updatedCovertFormatGroup = covertFormatGroup.map(
      (group) => group + '_2'
    );
    result = updatedCovertFormatGroup.includes(dataIndex);
  } else if (location.pathname.includes('payment-reports')) {
    const updatedCovertFormatGroup = covertFormatGroup.map(
      (group) => group + '_3'
    );
    result = updatedCovertFormatGroup.includes(dataIndex);
  } else if (location.pathname.includes('webinar-reports')) {
    const updatedCovertFormatGroup = covertFormatGroup.map(
      (group) => group + '_4'
    );
    result = updatedCovertFormatGroup.includes(dataIndex);
  } else {
    covertFormatGroup.forEach((group) => {
      if (dataIndex === group) {
        result = true;
      }
    });
  }

  return result;
};

export const checkMoneyDataType = (dataIndex: string) => {
  let result = false;
  if (location.pathname.includes('order-reports')) {
    const updatedMoneyFormatGroup = moneyFormatGroup.map(
      (group) => group + '_2'
    );
    result = updatedMoneyFormatGroup.includes(dataIndex);
  } else if (location.pathname.includes('payment-reports')) {
    const updatedMoneyFormatGroup = moneyFormatGroup.map(
      (group) => group + '_3'
    );
    result = updatedMoneyFormatGroup.includes(dataIndex);
  } else if (location.pathname.includes('webinar-reports')) {
    const updatedMoneyFormatGroup = moneyFormatGroup.map(
      (group) => group + '_4'
    );
    result = updatedMoneyFormatGroup.includes(dataIndex);
  } else {
    moneyFormatGroup.forEach((group) => {
      if (dataIndex === group) {
        result = true;
      }
    });
  }
  return result;
};
export const checkSummaryDataType = (dataIndex: string) => {
  let result = false;
  if (location.pathname.includes('order-reports')) {
    const updatedSummary = summaryFormatGroup.map((group) => group + '_2');
    result = updatedSummary.includes(dataIndex);
  } else if (location.pathname.includes('payment-reports')) {
    const updatedSummary = summaryFormatGroup.map((group) => group + '_3');
    result = updatedSummary.includes(dataIndex);
  } else if (location.pathname.includes('webinar-reports')) {
    const updatedSummary = summaryFormatGroup.map((group) => group + '_4');
    result = updatedSummary.includes(dataIndex);
  } else {
    summaryFormatGroup.forEach((group) => {
      if (dataIndex === group) {
        result = true;
      }
    });
  }
  return result;
};

export const checkPercentDataType = (dataIndex: string) => {
  let result = false;
  if (location.pathname.includes('order-reports')) {
    const updatedPercent = percentFormatGroup.map((group) => group + '_2');
    result = updatedPercent.includes(dataIndex);
  } else if (location.pathname.includes('payment-reports')) {
    const updatedPercent = percentFormatGroup.map((group) => group + '_3');
    result = updatedPercent.includes(dataIndex);
  } else if (location.pathname.includes('webinar-reports')) {
    const updatedPercent = percentFormatGroup.map((group) => group + '_4');
    result = updatedPercent.includes(dataIndex);
  } else {
    percentFormatGroup.forEach((group) => {
      if (dataIndex === group) {
        result = true;
      }
    });
  }
  return result;
};

export const checkAverageDataType = (dataIndex: string) => {
  let result = false;
  if (location.pathname.includes('order-reports')) {
    const updatedAverage = averageFormatGroup.map((group) => group + '_2');
    result = updatedAverage.includes(dataIndex);
  } else if (location.pathname.includes('payment-reports')) {
    const updatedAverage = averageFormatGroup.map((group) => group + '_3');
    result = updatedAverage.includes(dataIndex);
  } else if (location.pathname.includes('webinar-reports')) {
    const updatedAverage = averageFormatGroup.map((group) => group + '_4');
    result = updatedAverage.includes(dataIndex);
  } else {
    averageFormatGroup.forEach((group) => {
      if (dataIndex === group) {
        result = true;
      }
    });
  }

  return result;
};
const removeNbsp = (value: string) => {
  return value.replace(/&nbsp;/g, ' ').replace(/<[^>]*>/g, '');
};
/**
 * Схлопывает выбранные оси группировки в формат `groupings_names` для бэка.
 *
 * Переключатель «Группы регистраций» (dataKey `custom_group_set`) сворачивает ВСЕ
 * группы набора в ОДИН вложенный массив (из `memberKeys`) на своей позиции.
 * Отдельные группы «Выбор групп регистраций» (dataKey `custom_group_<id>`) и любые
 * обычные оси остаются строками — каждая свой уровень.
 * Пример: [reg_utm_source, custom_group_set(memberKeys:[c2,c3]), custom_group_9]
 *   → ["reg_utm_source", ["custom_group_2", "custom_group_3"], "custom_group_9"]
 */
// Суффикс группы по типу отчёта. Обычные оси этих отчётов несут суффикс
// (reg_utm_source_2 / _3), и бэк резолвит группу в ту же ось с суффиксом
// (custom_group_reg_2 / _3 — атрибуция по дате заказа/платежа).
// Заказы (_2), платежи (_3), вебинары (_4) готовы на бэке.
const groupReportSuffix = (): string => {
  const pathname = location.pathname;
  if (pathname.includes('order-reports')) return '_2';
  if (pathname.includes('payment-reports')) return '_3';
  if (pathname.includes('webinar-reports')) return '_4';
  return '';
};

// Суффикс группы по ТИПУ отчёта (не по pathname). Нужен там, где браузер не на
// странице отчёта — прежде всего виджеты дашборда (pathname = /dashboards), где
// сущность запроса определяется суффиксом осей, а не адресом. В обычных отчётах
// reportType совпадает с pathname, поэтому результат тот же.
export const groupSuffixByReportType = (reportType?: string): string => {
  switch (reportType) {
    case 'order': return '_2';
    case 'payment': return '_3';
    case 'webinar': return '_4';
    default: return '';
  }
};

// Суффикс добавляем ТОЛЬКО к ключам групп (custom_group_<id>). Обычные оси
// (reg_utm_source_2 и т.п.) суффикс уже несут — их regex не матчит, не трогаем.
const withGroupSuffix = (key: string, suffix: string): string =>
  /^custom_group_\d+$/.test(key) ? key + suffix : key;

export const buildGroupingsNames = (
  groupings: Array<{ dataKey?: string; memberKeys?: string[] }>,
  groupFilter?: GroupAxisFilter,
  // Явный суффикс группы (из типа виджета дашборда). Если не передан — берём из
  // pathname (обычные страницы отчётов), сохраняя прежнее поведение.
  reportSuffix?: string
): Array<string | string[]> => {
  const suffix = reportSuffix !== undefined ? reportSuffix : groupReportSuffix();
  // Приводим уровни к активному фильтру групп (та же чистка, что и на бэке):
  // отфильтрованный набор сужается, одиночная группа не по фильтру убирается.
  const source = narrowGroupingsByGroupFilter(groupings, groupFilter);
  const result: Array<string | string[]> = [];
  for (const col of source) {
    const key = col?.dataKey;
    if (!key) continue;
    if (key === CUSTOM_GROUP_SET_KEY) {
      // Свёрнутый набор всех групп → один вложенный массив ключей групп.
      const members = Array.isArray(col?.memberKeys) && col.memberKeys.length
        ? col.memberKeys
        : [ key ];
      result.push(members.map((memberKey) => withGroupSuffix(memberKey, suffix)));
    } else {
      result.push(withGroupSuffix(key, suffix));
    }
  }
  return result;
};

// Имя узла-сущности «Группы регистраций» (дубль REGISTRATION_GROUPS_NODE_NAME
// из modal-settings/config.ts — прямой импорт создал бы цикл config↔reports).
export const REGISTRATION_GROUPS_DISPLAY_NAME = 'Группы регистраций';
// Синтетический dataKey уровня-набора схлопнутых групп (для отображения).
export const CUSTOM_GROUP_SET_KEY = 'custom_group_set';

/**
 * Компактная метка «когда связь группы последний раз пересобиралась»
 * (membership_synced_at). Вход — строка вида "2026-08-10 15:38:49" (МСК) или null.
 * Возврат: { short: "10.08 15:38", full: "2026-08-10 15:38:49 (МСК)" } либо null,
 * если группа ещё ни разу не пересобиралась / значение не распознано.
 */
export const formatMembershipSynced = (
  value?: string | null
): { short: string; full: string } | null => {
  if (!value || typeof value !== 'string') return null;
  const matched = value.match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/);
  if (!matched) return { short: value, full: `${value} (МСК)` };
  const [ , , month, day, hours, minutes ] = matched;
  return { short: `${day}.${month} ${hours}:${minutes}`, full: `${value} (МСК)` };
};

/** Фильтр по группам регистраций: id включаемых/исключаемых групп. */
export interface GroupAxisFilter { include: number[]; exclude: number[]; }

/** id группы из dataKey уровня 'custom_group_<id>' (не 'custom_group_set') — иначе null. */
const customGroupIdFromKey = (dataKey?: string): number | null => {
  if (typeof dataKey !== 'string') return null;
  if (dataKey === CUSTOM_GROUP_SET_KEY) return null;
  const matched = dataKey.match(/^custom_group_(\d+)$/);
  return matched ? Number(matched[1]) : null;
};

/**
 * Достаёт include/exclude группы из активных фильтров:
 * `app_custom_groups` — включение, `not_app_custom_groups` — исключение
 * (режим «исключить» = префикс `not_`). `checkeds[].id` может прийти строкой.
 */
export const resolveGroupAxisFilter = (filters: any[] | undefined | null): GroupAxisFilter => {
  const include: number[] = [];
  const exclude: number[] = [];
  (Array.isArray(filters) ? filters : []).forEach((filter: any) => {
    const ids = Array.isArray(filter?.checkeds)
      ? filter.checkeds.map((checked: any) => Number(checked?.id)).filter((n: number) => Number.isFinite(n))
      : [];
    if (filter?.id === 'app_custom_groups') include.push(...ids);
    else if (filter?.id === 'not_app_custom_groups') exclude.push(...ids);
  });
  return { include, exclude };
};

/**
 * Приводит уровни группировки к фильтру по группам (точная копия бэкового
 * applyGroupFilterToAxis): свёрнутый набор `custom_group_set` сужается до
 * разрешённых групп, одиночный уровень-группа не по фильтру удаляется
 * (следующие уровни сдвигаются вверх). Если фильтр по группам не задан —
 * возвращает `groupings` как есть. НЕ мутирует исходный массив/колонки.
 */
export const narrowGroupingsByGroupFilter = (
  groupings: any[],
  groupFilter?: GroupAxisFilter
): any[] => {
  const include = groupFilter?.include ?? [];
  const exclude = groupFilter?.exclude ?? [];
  if (include.length === 0 && exclude.length === 0) return groupings;

  const includeSet = new Set(include);
  const excludeSet = new Set(exclude);
  const result: any[] = [];

  for (const col of Array.isArray(groupings) ? groupings : []) {
    const key = col?.dataKey;

    if (key === CUSTOM_GROUP_SET_KEY) {
      const baseIds = include.length > 0
        ? include
        : (Array.isArray(col?.memberKeys) ? col.memberKeys : [])
            .map((memberKey: string) => customGroupIdFromKey(memberKey))
            .filter((n: number | null): n is number => n !== null);
      const allowed = baseIds.filter((id: number) => !excludeSet.has(id));
      if (allowed.length === 0) continue; // весь набор отфильтрован — уровень убираем
      result.push({ ...col, memberKeys: allowed.map((id: number) => `custom_group_${id}`) });
      continue;
    }

    const groupId = customGroupIdFromKey(key);
    if (groupId !== null) {
      const passInclude = include.length === 0 || includeSet.has(groupId);
      if (!passInclude || excludeSet.has(groupId)) continue; // группа не по фильтру — уровень убираем
      result.push(col);
      continue;
    }

    result.push(col); // обычная ось
  }

  return result;
};

/**
 * Схлопнутый для ОТОБРАЖЕНИЯ список уровней группировки: параллель к
 * `buildGroupingsNames`, но возвращает объекты-уровни. Все группы регистрации
 * (`custom_group_*`) сливаются в ОДИН синтетический уровень «Группы регистраций»
 * на позиции первой такой группы; обычные оси остаются как есть. Нужен, чтобы
 * заголовок/имя схлопнутого уровня в таблице показывались как имя сущности,
 * а не перечислением отдельных «Группа 1»/«Группа 2».
 */
export const buildDisplayGroupings = (
  groupings: any[],
  groupFilter?: GroupAxisFilter
): any[] => {
  // Та же чистка под фильтр групп, что и в buildGroupingsNames, — чтобы глубина
  // отображаемых уровней совпадала с отправляемой на бэк осью.
  const source = narrowGroupingsByGroupFilter(groupings, groupFilter);
  const result: any[] = [];
  for (const col of source) {
    const key = col?.dataKey;
    // Переключатель набора (custom_group_set) → один синтетический уровень «Группы
    // регистраций». Отдельные группы (custom_group_<id>) и обычные оси показываются
    // как есть — каждая своим уровнем со своим именем.
    if (key === CUSTOM_GROUP_SET_KEY) {
      result.push({
        name: REGISTRATION_GROUPS_DISPLAY_NAME,
        label: REGISTRATION_GROUPS_DISPLAY_NAME,
        dataKey: CUSTOM_GROUP_SET_KEY,
        isGroupSet: true,
      });
    } else {
      result.push(col);
    }
  }
  return result;
};

export const prepareRows = ({ rows, parent, groupings, flatMode, groupFilter }: any) => {
  // Эффективная глубина = длина схлопнутого groupings_names: несколько групп
  // регистрации (custom_group_*) дают ОДИН уровень отчёта, а не по уровню каждая.
  // groupFilter приводит ось к активному фильтру групп (уровень-группа не по
  // фильтру выкидывается) — иначе глубина строк разошлась бы с displayGroupings.
  // Считаем один раз на весь набор строк (не в map).
  const effectiveDepth = buildGroupingsNames(groupings, groupFilter).length;
  return rows.map((item: any, index: number) => {
    if (location.pathname === '/users/payment-reports') {
       item.group = item.group?.replace(/&nbsp;/g, ' ');
    }
    const path = parent ? [ ...parent.clientData.path ] : [];
    const pathLabel = parent ? [ ...parent.clientData.pathLabel ] : [];
    path.push(item.id);
    pathLabel.push(item.group);

    const expandable = effectiveDepth > path.length;
    // В flatMode id содержит только значение первого уровня группировки,
    // из-за чего pathStr может совпадать у разных строк (например, все "mail > ..." получают pathStr="mail").
    // Добавляем индекс для уникальности, сохраняя оригинальный path для API-запросов.
    const pathStr = flatMode ? `${path.join('-')}_flat_${index}` : path.join('-');
    return {
      ...item,
      pathStr,
      pathStr2: uuidv4(),
      children: expandable ? [] : null,
      clientData: { path, pathLabel, loading: false, downloaded: false },
    };
  });
};

export const getCountDaysFromPeriod = (period: any) => {
  return moment(period.end).diff(moment(period.start), 'days');
};

export const parseTableData = (data: any[]) => {
  if (!data) return [];
  return data.map((dataItem) => {
    const _dataItem = { ...dataItem };

    if (typeof _dataItem.name === 'object') {
      _dataItem.name = (
        <span className="group-item-icon-wrapper">
          {dataItem.label}
          <span style={{ marginRight: 10, marginLeft: 5 }}>
            <Tooltip
              placement="bottom"
              title={
                <div
                  className="page-title-tooltip-text"
                  dangerouslySetInnerHTML={{ __html: dataItem.description }}
                />
              }
              color="#fff"
              zIndex={9999}
            >
              <QuestionCircleOutlined style={{ color: '#ccc', fontSize: 16 }} />
            </Tooltip>
          </span>
        </span>
      );
    }

    return _dataItem;
  });
};

/**
 * Получает данные для столбчатой диаграммы по группировке и метрике.
 * @returns значение метрики или null, если данные не найдены
 */
export const getGroupDataForBarChart = (
  isCompare: boolean,
  tableData: any,
  percentages: any,
  metric: any,
  group: any
): number | string | null => {
  // Вспомогательная функция для поиска строки
  const findRowData = () => {
    // Сначала проверяем специальные случаи
    if (group?.pathStr === 'total') {
      return tableData.total;
    }
    if (group?.pathStr === 'total_choosen') {
      return tableData.totalChosen?.row || tableData.totalChosen;
    }
    
    // Ищем в основных данных
    let findRow = findRowRecurse(tableData.data, group?.pathStr);
    
    // Если не нашли в data, попробуем найти в totalChosen.active
    if (!findRow && tableData.totalChosen?.active) {
      findRow = tableData.totalChosen.active.find((item: any) => item.pathStr === group?.pathStr);
    }
    
    return findRow;
  };

  const findRow = findRowData();
  
  // Если строка не найдена, возвращаем null (не 0!)
  // Это позволяет отличить "данные не загружены" от "значение равно 0"
  if (!findRow) {
    return null;
  }

  const metricKey = metric.dataKey || metric.name;

  if (isCompare) {
    if (percentages.includes(metricKey)) {
      const totalValue = tableData.total?.[metricKey];
      if (!totalValue) return null;
      return parseFloat(
        String((findRow[metricKey] / totalValue) * 100)
      ).toFixed(0);
    }

    let _findRow = findRow[metricKey];
    let resRow;

    // Метрика второго периода: в таблице оба периода лежат в одной ячейке
    // основной метрики строкой вида «29 - 229», второе число — второй период
    if (isCompareMetricKey(metricKey)) {
      const splittedGroupRowData = findRow[toBaseMetricKey(metricKey)];
      if (splittedGroupRowData) {
        if (typeof splittedGroupRowData === 'number') {
          resRow = splittedGroupRowData;
        } else {
          const splittedRowData = splittedGroupRowData.includes('%')
            ? splittedGroupRowData.split('% ')[1]
            : splittedGroupRowData.includes('=')
            ? splittedGroupRowData.split('= ')[1]
            : splittedGroupRowData.includes('+')
            ? splittedGroupRowData.split('+ ')[1]
            : splittedGroupRowData.split('- ')[1];

          resRow = parseInt(splittedRowData);
        }
      } else {
        resRow = 0;
      }
    } else {
      if (_findRow !== undefined && _findRow !== null) {
        if (typeof _findRow === 'number') {
          resRow = _findRow;
        } else {
          const splittedRowData = _findRow.split(' -')[0];
          resRow = parseInt(splittedRowData);
        }
      } else {
        resRow = 0;
      }
    }

    return resRow;
  } else {
    if (percentages.includes(metricKey)) {
      const totalValue = tableData.total?.[metricKey];
      if (!totalValue) return null;
      return parseFloat(
        String((findRow[metricKey] / totalValue) * 100)
      ).toFixed(0);
    }

    return findRow[metricKey];
  }
};

export const numberFormat = (n: string) => {
  return ((parseInt(n) || 0) + '').replace(
    /(\d)(?=(\d\d\d)+([^\d]|$))/g,
    '$1 '
  );
};

export const toolFormat = (a: any, b: any) => {
  let num = ((parseInt(a) || 0) + '').replace(
    /(\d)(?=(\d\d\d)+([^\d]|$))/g,
    '$1 '
  );
  if ([ 9 ].includes(b)) {
    return num + ' %';
  }
  return num;
};

export const formatBannerData = (
  bannerTitle: string,
  data: any,
  formatterFn: any
) => {
  switch (bannerTitle) {
    case 'payments_sum':
      data = formatterFn(data);
      break;
    case 'pay_per_user':
      data = formatterFn(data);
      break;
    case 'commentators_part':
      data = numberFormat(data);
      break;
    case 'commentators_count':
      data = numberFormat(data);
      break;
    case 'webinar_users_count_desktop':
      data = numberFormat(data);
      break;
    case 'webinar_users_count_mobile':
      data = numberFormat(data);
      break;
    case 'webinar_users_count_tablet':
      data = numberFormat(data);
      break;
    case 'comments_count':
      data = numberFormat(data);
      break;
    case 'orders_creators_count':
      data = numberFormat(data);
      break;
    case 'orders_count':
      data = numberFormat(data);
      break;
    case 'complete_orders_count':
      data = numberFormat(data);
      break;
    case 'clickers_count':
      data = numberFormat(data);
      break;
    case 'clicks_count':
      data = numberFormat(data);
      break;
    default:
  }

  return data;
};

export const moneyFormat = (float: string) => {
  return parseFloat(float)
    .toFixed(2)
    .replace(/(\d)(?=(\d{3})+\.)/g, '$1 ')
    .replace('.', ',');
};

export const webinarPredictDotsCount = (filters: any[]) => {
  let hoursCount = 3;

  const hoursFilter = filters.find((item) => item.id === 'time_webinar');

  if (hoursFilter) {
    const { start, end } = hoursFilter;

    hoursCount = moment(end, [ moment.ISO_8601, 'HH:mm' ]).diff(
      moment(start, [ moment.ISO_8601, 'HH:mm' ]),
      'hours'
    );
  }

  if (hoursCount > 2) {
    return 2;
  } else {
    return 1;
  }
};

export const observeFiltersForWebinarLength = (filters: any[]) => {
  let hoursCount = 3;

  const hoursFilter = filters.find((item) => item.id === 'time_webinar');

  if (hoursFilter) {
    const { start, end } = hoursFilter;

    hoursCount = moment(end, [ moment.ISO_8601, 'HH:mm' ]).diff(
      moment(start, [ moment.ISO_8601, 'HH:mm' ]),
      'hours'
    );
  }

  if (hoursCount > 2) {
    return 2;
  } else {
    return 1;
  }
};

export const searchFilter = (item: any, searchValue: string) => {
  if (item.name) {
    return removeNbsp(item.name)
      .toLowerCase()
      .includes(searchValue.toLowerCase());
  }
  return false;
};


export const handleReportPeriod = (period?: any) => {
  // Если период не передан или не содержит нужных свойств, возвращаем значения по умолчанию
  if (!period || !period.start || !period.end) {
    const defaultStart = moment().subtract(7, 'days').format('YYYY-MM-DD');
    const defaultEnd = moment().format('YYYY-MM-DD');
    return { 
      start: defaultStart, 
      end: defaultEnd, 
      interval: period?.interval || period?.select || 'day' 
    };
  }

  // Обработка различных форматов даты (сохраняем время если оно есть)
  let start = moment(period.start).isValid()
    ? formatPeriodDate(period.start, period.withTime)
    : period.start;

  let end = moment(period.end).isValid()
    ? formatPeriodDate(period.end, period.withTime)
    : period.end;

  return {
    start,
    end,
    interval: period.interval || period.select || 'day'
  };
};

/**
 * Возвращает период "последние 7 дней" для использования в модальных окнах
 * @returns Объект с start и end в формате YYYY-MM-DD
 */
export const getLast7DaysPeriod = () => {
  const start = moment().subtract(7, 'days').format('YYYY-MM-DD');
  const end = moment().format('YYYY-MM-DD');
  return { start, end };
};
